import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AnalyticsProvider } from '../contexts/AnalyticsContext';
import PredictiveAnalytics from './PredictiveAnalytics';
import ReportGenerator from './ReportGenerator';
import ExecutiveReporting from './ExecutiveReporting';
import { BarChart3, Brain, FileText, Crown, TrendingUp, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const AdvancedAnalytics = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('predictive');

  const tabs = [
    {
      id: 'predictive',
      name: 'Predictive Analytics',
      icon: Brain,
      description: 'AI-powered forecasting and insights',
      component: PredictiveAnalytics,
      roles: ['employee', 'manager', 'hr', 'executive', 'admin']
    },
    {
      id: 'reports',
      name: 'Report Generator',
      icon: FileText,
      description: 'Custom reports and data export',
      component: ReportGenerator,
      roles: ['manager', 'hr', 'executive', 'admin']
    },
    {
      id: 'executive',
      name: 'Executive Dashboard',
      icon: Crown,
      description: 'Strategic insights and KPIs',
      component: ExecutiveReporting,
      roles: ['executive', 'admin']
    }
  ];

  // Filter tabs based on user role
  const filteredTabs = tabs.filter(tab => tab.roles.includes(user?.role));

  const ActiveComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;

  return (
    <AnalyticsProvider>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <BarChart3 className="mr-3 text-blue-600" size={32} />
                  Advanced Analytics & Reporting
                </h1>
                <p className="text-gray-600 mt-1">
                  Comprehensive business insights, predictive analytics, and custom reporting
                </p>
              </div>
              
              {/* User Role Badge */}
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {user?.role?.toUpperCase()} Access
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  {filteredTabs.length} analytics module{filteredTabs.length !== 1 ? 's' : ''} available
                </p>
              </div>
            </div>
          </div>

          {/* Feature Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white"
            >
              <div className="flex items-center mb-4">
                <Brain className="mr-3" size={24} />
                <h3 className="text-lg font-semibold">AI-Powered Insights</h3>
              </div>
              <p className="text-purple-100 text-sm">
                Machine learning algorithms analyze performance patterns to predict future trends and identify optimization opportunities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white"
            >
              <div className="flex items-center mb-4">
                <FileText className="mr-3" size={24} />
                <h3 className="text-lg font-semibold">Custom Reports</h3>
              </div>
              <p className="text-blue-100 text-sm">
                Generate tailored reports with drag-and-drop builders, export to multiple formats, and schedule automated delivery.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white"
            >
              <div className="flex items-center mb-4">
                <TrendingUp className="mr-3" size={24} />
                <h3 className="text-lg font-semibold">Executive Intelligence</h3>
              </div>
              <p className="text-yellow-100 text-sm">
                Strategic dashboards with KPI tracking, department comparisons, and organizational health metrics for leadership.
              </p>
            </motion.div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {filteredTabs.map((tab) => {
                  const IconComponent = tab.icon;
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                        isActive
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <IconComponent
                        className={`mr-2 ${
                          isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                        size={18}
                      />
                      <div className="text-left">
                        <div>{tab.name}</div>
                        <div className="text-xs font-normal text-gray-500">{tab.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Active Component */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow"
          >
            <div className="p-6">
              {ActiveComponent && <ActiveComponent />}
            </div>
          </motion.div>

          {/* Help Section */}
          <div className="mt-8 bg-gray-100 rounded-lg p-6">
            <div className="flex items-start">
              <Target className="text-blue-500 mr-3 mt-1" size={20} />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Getting Started with Advanced Analytics</h3>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>
                    <strong>Predictive Analytics:</strong> Use AI-powered insights to forecast performance trends, 
                    identify risks, and get data-driven recommendations for improvement.
                  </p>
                  {filteredTabs.some(tab => tab.id === 'reports') && (
                    <p>
                      <strong>Report Generator:</strong> Create custom reports using pre-built templates or 
                      build your own with the interactive report builder. Export to PDF, Excel, or CSV.
                    </p>
                  )}
                  {filteredTabs.some(tab => tab.id === 'executive') && (
                    <p>
                      <strong>Executive Dashboard:</strong> Access strategic KPIs, organizational health metrics, 
                      and department performance comparisons designed for leadership decision-making.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnalyticsProvider>
  );
};

export default AdvancedAnalytics;